import React from "react";
import { connect } from "react-redux";
import Infinite from "react-infinite";
import { withContentRect } from "react-measure";
import { PropTypes } from "prop-types";
import { OrderedMap } from "immutable";
import classNames from "classnames";
import { Helmet } from "react-helmet";

import {
  getTagsNextPageAsync,
  getTagsAsync,
  resetTagState,
  toggleTagSelection,
} from "settings/actions/tags";
import TagRow from "settings/components/TagRow";
import Search from "settings/components/Search";
import SettingsWrapper from "settings/components/SettingsWrapper";
import SettingsHeader from "settings/components/SettingsHeader";
import SettingsTable from "settings/components/SettingsTable";
import Spinner from "components/library/Spinner";
import NoResults from "components/library/NoResults";
import LinkButton from "components/library/LinkButton";
import WithPermissions from "components/library/WithPermissions";

import styles from "./Tags.css";

const INITIAL_TAG_ROW_HEIGHT = 70;

const MeasuredTagRow = withContentRect("client")(props => (
  <div ref={props.measureRef} key={props.key}>
    <TagRow {...props} />
  </div>
));
MeasuredTagRow.displayName = "MeasuredTagRow";

class Tags extends React.Component {
  constructor(props) {
    super(props);

    this.handleInfiniteLoad = this._handleInfiniteLoad.bind(this);
    this.handleSearch = this._handleSearch.bind(this);

    this.state = {
      infiniteLoading: false,
      elementHeights: Array.apply(null, Array(props.tags.size)).map(
        () => INITIAL_TAG_ROW_HEIGHT
      ),
    };
  }

  componentDidMount() {
    this.props.resetTagState();
    this.props.getTagsAsync({ ordering: "-stories,name" });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.tags !== this.props.tags) {
      this.setState({
        // Because children haven't been measured yet, set height initially to an approximate value: 70px
        elementHeights: Array.apply(null, Array(nextProps.tags.size)).map(
          () => INITIAL_TAG_ROW_HEIGHT
        ),
        edgeOffset: INITIAL_TAG_ROW_HEIGHT * 2,
      });
    }
  }

  _handleInfiniteLoad() {
    this.setState({ infiniteLoading: true });
    return this.props
      .getTagsNextPageAsync()
      .catch(() => this.setState({ edgeOffset: undefined }))
      .then(() => this.setState({ infiniteLoading: false }));
  }

  _handleSearch(query) {
    this.props.getTagsAsync({ ordering: "-stories,name", name: query });
  }

  isTagSelected(tag) {
    return this.props.selected[tag.id] || false;
  }

  selectedTagCount() {
    return Object.values(this.props.selected).filter(b => b).length;
  }

  updateSize(index, height) {
    const heights = [...this.state.elementHeights];
    heights[index] = height;
    this.setState({ elementHeights: heights });
  }

  render() {
    let tagList = [];
    const selectedTagCount = this.selectedTagCount();

    if (this.props.tags) {
      tagList = this.props.tags.toArray();
    }

    let infiniteTagList = (
      <div className={styles.noTags}>
        <NoResults message="No tags" />
      </div>
    );

    if (tagList.length) {
      infiniteTagList = (
        <SettingsTable>
          <Infinite
            useWindowAsScrollContainer
            elementHeight={this.state.elementHeights}
            infiniteLoadBeginEdgeOffset={this.state.edgeOffset}
            loadingSpinnerDelegate={
              <div className={styles.spinnerContainer}>
                <Spinner />
              </div>
            }
            onInfiniteLoad={this.handleInfiniteLoad}
            isInfiniteLoading={this.state.infiniteLoading}
            preloadBatchSize={Infinite.containerHeightScaleFactor(2)}
          >
            {tagList.map((tag, i) => {
              return (
                <MeasuredTagRow
                  tag={tag}
                  key={tag.id}
                  selected={this.isTagSelected(tag)}
                  onChange={() => this.props.toggleTagSelection(tag)}
                  onResize={contentRect =>
                    this.updateSize(i, contentRect.client.height)
                  }
                  permissions={this.props.permissions}
                />
              );
            })}
          </Infinite>
        </SettingsTable>
      );
    }

    return (
      <SettingsWrapper>
        <Helmet>
          <title>Tags</title>
        </Helmet>

        <SettingsHeader title="Tags">
          {selectedTagCount > 0 ? (
            <div className={styles.actions}>
              {selectedTagCount > 1 ? (
                //per the backend, add_tag is the correct permission for merging
                <WithPermissions required={["content.add_tag"]}>
                  <LinkButton
                    to="/settings/tags/merge"
                    type="secondary"
                    className={styles.merge}
                  >
                    Merge ({selectedTagCount})
                  </LinkButton>
                </WithPermissions>
              ) : null}
              <WithPermissions required={["content.delete_tag"]}>
                <LinkButton
                  to="/settings/tags/delete"
                  type="secondary"
                  className={styles.delete}
                >
                  Delete ({selectedTagCount})
                </LinkButton>
              </WithPermissions>
            </div>
          ) : null}
          <Search
            className="gtm-find-tag"
            placeholder={"Find a tag"}
            onChange={this.handleSearch}
          />
          <WithPermissions required={["content.add_tag"]}>
            <LinkButton to="/settings/tags/add" className={styles.addButton}>
              + New tag
            </LinkButton>
          </WithPermissions>
        </SettingsHeader>

        {this.props.isLoadingTags ? (
          <div className={classNames(styles.noTags, styles.spinnerContainer)}>
            <Spinner />
          </div>
        ) : (
          infiniteTagList
        )}
      </SettingsWrapper>
    );
  }
}

const mapStateToProps = state => ({
  tags: state.settings.tags.tags,
  selected: state.settings.tags.selected,
  isLoadingTags: state.settings.tags.isLoadingTags,
  permissions: state.user.permissions,
});

const mapDispatchToProps = {
  getTagsAsync,
  getTagsNextPageAsync,
  resetTagState,
  toggleTagSelection,
};

Tags.propTypes = {
  resetTagState: PropTypes.func.isRequired,
  toggleTagSelection: PropTypes.func.isRequired,
  getTagsAsync: PropTypes.func.isRequired,
  getTagsNextPageAsync: PropTypes.func.isRequired,
  tags: PropTypes.instanceOf(OrderedMap),
  selected: PropTypes.object.isRequired,
  isLoadingTags: PropTypes.bool.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Tags);
